import React from 'react';
import { Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';

import styled from 'styled-components';

import { ComingSoonFeatureKey } from 'components/ComingSoonFeatures/constants';
import { COMPANY_COMMUNITY_RELATION_TYPE } from 'constants/company';
import { canRegenerateAICI } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import AICIAnalysisSwitcher from './AICIAnalysisSwitcher';
import AICIHeader from './AICIHeader';
import AICIRegenerate from './AICIRegenerate';
import CompanyAICIPageContainer from './CompanyAICIPageContainer';
import { AICI_ANALYSIS_SCOPE, AICI_SWITCHER_VIEW, AICISwitcherView } from './constants';

const SPEED_TO_LEAD_PATH_SEGMENT = '/speed-to-lead';

const Container = styled.div`
  width: 100%;
  min-width: 0;
`;

const CommunityPageContainer = styled(CompanyAICIPageContainer)`
  max-width: 1170px;
`;

// Wraps the Overall Market Position, Speed to Lead, and Coming Soon tab routes so the header and tab switcher stay
// mounted while switching between them - only the routed content below the switcher (the Outlet) should remount.
const AICISwitcherLayout = () => {
  const location = useLocation();
  const { currentUser } = useAppSelector((state) => state.auth);
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { feature } = useParams<{ feature?: ComingSoonFeatureKey }>();
  const [searchParams] = useSearchParams();
  const scope = searchParams.get('communityId') ? AICI_ANALYSIS_SCOPE.COMMUNITY : AICI_ANALYSIS_SCOPE.COMPANY;
  const activeView: AICISwitcherView =
    feature ??
    (location.pathname.includes(SPEED_TO_LEAD_PATH_SEGMENT)
      ? AICI_SWITCHER_VIEW.SPEED_TO_LEAD
      : AICI_SWITCHER_VIEW.OVERALL);

  const PageContainer = scope === AICI_ANALYSIS_SCOPE.COMMUNITY ? CommunityPageContainer : CompanyAICIPageContainer;

  const canRegenerate =
    scope === AICI_ANALYSIS_SCOPE.COMMUNITY &&
    activeView === AICI_SWITCHER_VIEW.OVERALL &&
    selectedCommunity?.type === COMPANY_COMMUNITY_RELATION_TYPE.COMMUNITY &&
    canRegenerateAICI(currentUser);

  return (
    <Container>
      <AICIHeader>{canRegenerate && <AICIRegenerate />}</AICIHeader>
      <PageContainer>
        <AICIAnalysisSwitcher activeView={activeView} scope={scope} />
        <Outlet />
      </PageContainer>
    </Container>
  );
};

export default AICISwitcherLayout;
